import { type MouseEventHandler, useCallback, type FC } from 'react';
import {
  Box,
  Divider,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
  DrawerProps,
} from '@mui/material';
import { isNil } from 'lodash';
import { Assignment, Person, Settings } from '@mui/icons-material';
import { NavLink } from 'react-router';

import type { UserProfile } from '#packages/web/types/user-profile';

import {
  SignedInUser,
  type SignedInUserProps,
} from './components/signed-in-user';
import { GuestUser, type GuestUserProps } from './components/guest-user';

export type AppDrawerProps = DrawerProps & {
  /**
   * The Signed-in user profile.
   */
  userProfile?: UserProfile;

  /**
   * Callback function when the sign in button is clicked.
   */
  onSignInClick: GuestUserProps['onSignInClick'];

  /**
   * Callback function when the sign out button is clicked.
   */
  onSignOutClick: SignedInUserProps['onSignOutClick'];

  /**
   * The application routes.
   * (Used for navigation links)
   */
  routes: {
    /**
     * The route for the audits list page.
     */
    auditsList: string;

    /**
     * The user profile page route.
     */
    userProfile: string;

    /**
     * The user settings page route.
     */
    userSettings: string;
  };

  /**
   * The version of the application.
   */
  appVersion: string;
};

export const AppDrawer: FC<AppDrawerProps> = ({
  userProfile,
  onSignInClick,
  onSignOutClick,

  routes,
  appVersion,

  ...props
}) => {
  const signedIn = !isNil(userProfile);

  const onNavigationClick = useCallback<MouseEventHandler<HTMLUListElement>>(
    (e) => props.onClose?.(e, 'backdropClick'),
    [props.onClose],
  );

  return (
    <Drawer anchor="left" {...props}>
      <Stack
        flex={1}
        justifyContent="space-between"
        sx={{ minWidth: '15rem', width: '15rem' }}>
        <Box>
          {/* App Title */}
          <Typography
            variant="h5"
            component="div"
            textAlign="center"
            sx={{ py: 2 }}>
            JOBI Data Collection
          </Typography>
          <Divider sx={{ mb: 2 }} />

          {/* User Information */}
          {signedIn !== true && <GuestUser onSignInClick={onSignInClick} />}
          {signedIn === true && (
            <SignedInUser
              userProfile={userProfile!}
              onSignOutClick={onSignOutClick}
            />
          )}
          <Divider sx={{ my: 1 }} />

          {/* Navigation links */}
          <List onClick={onNavigationClick}>
            <ListItem disablePadding>
              <ListItemButton component={NavLink} to={routes.auditsList}>
                <ListItemIcon>
                  <Assignment />
                </ListItemIcon>
                <ListItemText primary="Audits" />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton component={NavLink} to={routes.userProfile}>
                <ListItemIcon>
                  <Person />
                </ListItemIcon>
                <ListItemText primary="Profile" />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton component={NavLink} to={routes.userSettings}>
                <ListItemIcon>
                  <Settings />
                </ListItemIcon>
                <ListItemText primary="Settings" />
              </ListItemButton>
            </ListItem>
          </List>
        </Box>

        {/* Miscellaneous */}
        <Box>
          <Divider />
          <Typography variant="caption" component="div" textAlign="center">
            {appVersion}
          </Typography>
        </Box>
      </Stack>
    </Drawer>
  );
};
